import React, { useMemo, useState, useEffect } from 'react';
import { Clock } from 'lucide-react';
import trainingApi from './api';
import QuizResults from './components/QuizResults';
import { useTrainingLang } from './i18n/TrainingLangContext';

const shuffle = (arr) => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
};

const Quiz = ({ training, questions, attemptId, relatedSurvey, onRetry, onExit }) => {
  const { t } = useTrainingLang();
  const orderedQuestions = useMemo(
    () => (training.randomize_questions ? shuffle(questions) : questions),
    [training.randomize_questions, questions]
  );

  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(training.time_limit_seconds || null);

  useEffect(() => {
    if (!training.time_limit_seconds || result) return undefined;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t === null) return null;
        if (t <= 1) {
          clearInterval(interval);
          handleSubmit(true);
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [training.time_limit_seconds, result]);

  const handleSubmit = async (forced = false) => {
    if (submitting) return;
    if (!forced) {
      const unanswered = orderedQuestions.filter((q) => !(q.id in answers));
      if (unanswered.length > 0) {
        if (!window.confirm(t('unansweredConfirm', { count: unanswered.length }))) {
          return;
        }
      }
    }
    setSubmitting(true);
    setError('');
    try {
      const data = await trainingApi.submitAttempt(attemptId, answers);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (result) {
    return (
      <QuizResults
        result={result}
        questions={orderedQuestions}
        relatedSurvey={relatedSurvey}
        onRetry={onRetry}
        onExit={onExit}
      />
    );
  }

  return (
    <div className="max-w-3xl mx-auto p-6 text-slate-100">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold">{t('quizFor', { title: training.title })}</h2>
        {timeLeft !== null && (
          <div className="flex items-center gap-2 px-3 py-1.5 rounded bg-slate-800 border border-slate-700">
            <Clock className="w-4 h-4 text-amber-400" />
            <span className="font-mono">{formatTime(timeLeft)}</span>
          </div>
        )}
      </div>

      <p className="text-sm text-slate-400 mb-6">
        {t('passingScoreLine', {
          pct: training.pass_threshold_pct,
          count: orderedQuestions.length,
        })}
      </p>

      <ol className="space-y-5">
        {orderedQuestions.map((q, i) => (
          <li key={q.id} className="bg-slate-800 border border-slate-700 rounded-lg p-4">
            <div className="font-medium mb-3">{i + 1}. {q.question_text}</div>
            <div className="space-y-2">
              {q.options.map((opt) => (
                <label
                  key={opt.id}
                  className={`flex items-center gap-3 p-2 rounded cursor-pointer border ${
                    answers[q.id] === opt.id
                      ? 'border-amber-500 bg-amber-900/20'
                      : 'border-slate-700 hover:bg-slate-700/40'
                  }`}
                >
                  <input
                    type="radio"
                    name={`q_${q.id}`}
                    value={opt.id}
                    checked={answers[q.id] === opt.id}
                    onChange={() => setAnswers({ ...answers, [q.id]: opt.id })}
                  />
                  <span>{opt.option_text}</span>
                </label>
              ))}
            </div>
          </li>
        ))}
      </ol>

      {error && (
        <div className="mt-4 p-3 rounded bg-rose-900/40 border border-rose-700 text-rose-200 text-sm">
          {error}
        </div>
      )}

      <div className="mt-6 flex justify-end gap-3">
        <button
          type="button"
          onClick={onExit}
          className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600"
        >
          {t('cancel')}
        </button>
        <button
          type="button"
          onClick={() => handleSubmit(false)}
          disabled={submitting}
          className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-60"
        >
          {submitting ? t('submitting') : t('submitQuiz')}
        </button>
      </div>
    </div>
  );
};

export default Quiz;
